import {MessageLogger} from "./MessageLogger";
import {TakipAcStatusEnum} from "./parser/TakipAcStatusEnum";
import app from "@/main";
import JSZip from "jszip";
import {Mixins} from "./Mixins";
import {EvrakGonderCevap} from "../uyap/DosyaAcilisEvrakGonder";
import {matchUyapError, takipTalebiIcerikRetHatasi, takipTalebiIcerikRetMi} from "../uyap/uyapErrorMatchers";
import {istekOlcumu} from "../IstekOlcumu";
import {PlayerDurum} from "../store/modules/GorevYoneticisiTypes";
import {
    ACILMIS_TAKIP_STATUS,
    AcilmisTakipBilgisi,
    acilmisTakipBilgisiOlustur,
    acilmisTakipHataMesaji,
    HashKontrolKayit,
    isAcilmisTakipKaydi,
    isOdemeDeneniyorKaydi,
    odemeDeneniyorHataMesaji,
    ODEME_DENENIYOR_STATUS,
    TEVZI_STATUS
} from "./AcilmisTakipHatasi";

interface HashKontrolCevap {
    success: boolean,
    message: string,
    data: HashKontrolKayit | null
}

/**
 * DEV-3880: UYAP imzalı takip talebini zaman zaman "Takip Talebi evrağı içeriğinde değişiklik
 * yapmadan imzalayıp göndermeniz gerekmektedir." diye reddediyor; evrak değiştirilmeden
 * imzalanıyor ve aynı takip sonraki denemede kabul edilebiliyor. Ret gelince aynı dosyada takip
 * talebi yeniden indirilip imzalanır ve gönderilir; ilk gönderim dahil en fazla bu kadar deneme.
 */
export const TAKIP_TALEBI_DENEME_SAYISI = 3;
/**
 * Tekrar denenip yine hiç kabul alınamayan bu kadar dosya üst üste gelirse sigorta atar: aynı
 * dosyada tekrar işe yaramıyorsa UYAP'a boşuna istek gitmesin. Deneme başına ~%50 kabulde bir
 * dosyanın üç denemede de reddedilme olasılığı ~%25; 8 dosya üst üste bunun boşuna atması çok düşük.
 */
export const TAKIP_TALEBI_SIGORTA_ESIGI = 8;
/** Sigorta atıkken her bu kadarıncı reddedilen dosya yine tekrar denenir; kabul gelirse sigorta kalkar. */
export const TAKIP_TALEBI_SIGORTA_YOKLAMA = 10;
let sonucsuzTekrar = 0;
let sigortaAtikkenRet = 0;

export function takipTalebiSigortasiAtikMi(): boolean {
    return sonucsuzTekrar >= TAKIP_TALEBI_SIGORTA_ESIGI;
}

/** Testler için. */
export function takipTalebiSigortasiniSifirla(): void {
    sonucsuzTekrar = 0;
    sigortaAtikkenRet = 0;
}

/** İlk ret gelince bu dosyada kaç gönderim yapılacağı; sigorta atıkken yalnız yoklama dosyası tekrar denenir. */
function retSonrasiDenemeHakki(): number {
    if (!takipTalebiSigortasiAtikMi())
        return TAKIP_TALEBI_DENEME_SAYISI;
    sigortaAtikkenRet++;
    return sigortaAtikkenRet % TAKIP_TALEBI_SIGORTA_YOKLAMA == 0 ? TAKIP_TALEBI_DENEME_SAYISI : 1;
}

/** Görevden JSON ile gelen {} ve [] de dolu sayılmasın. */
function gecerliUdf(udf: any): boolean {
    return udf instanceof Blob && udf.size > 0;
}

function blobBaytlari(blob: Blob): Promise<Uint8Array> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(reader.error);
        reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
        reader.readAsArrayBuffer(blob);
    });
}

/**
 * Yeniden indirilen evrak öncekiyle aynı mı diye bakmak için içerik özeti (FNV-1a); okunamazsa null.
 * Evrak indirilirken zip'e konuyor ve zip başlığında o anın saati var; özet içteki content.xml'den alınır.
 */
async function udfOzeti(udf: any): Promise<string | null> {
    if (!gecerliUdf(udf))
        return null;
    try {
        let veri = await blobBaytlari(udf);
        try {
            const icerik = (await JSZip.loadAsync(veri)).file("content.xml");
            if (icerik)
                veri = await icerik.async("uint8array");
        } catch (e) {
            // Zip değilse dosyanın tamamı özetlenir.
        }
        let ozet = 0x811c9dc5;
        for (let i = 0; i < veri.length; i++) {
            ozet ^= veri[i];
            ozet = Math.imul(ozet, 0x01000193) >>> 0;
        }
        return veri.length + ":" + ozet.toString(16);
    } catch (e) {
        return null;
    }
}

export default class TakipAcInjectable extends MessageLogger {
    status: TakipAcStatusEnum = TakipAcStatusEnum.QUERY_PENDING;
    imzasizUdf !: Blob;
    imzaliUdf !: Blob;
    vekalet !: Blob;
    takipAcilisEvraklariResponse!: EvrakGonderCevap;

    takipBilgileri = {
        dosyaEsasNo: "",
        icraDairesi: ""
    };
    // Hash'e ait takip UYAP'ta zaten açılmışsa dolar. Dolu ise hash silinemez.
    acilmisTakipBilgisi: AcilmisTakipBilgisi | null = null;
    // UYAP'ta takip açıldı ama icraPro kartına işlenemedi. Görev hatalı değil,
    // eksik biter (TaskDurum.BITTI_EKSIK).
    icraProSenkronHatasi: boolean = false;
    // UYAP'ta takip açıldı ama hash kaydı "acildi" durumuna çekilemedi (DEV-3302).
    // Görev hatalı değil, eksik biter (TaskDurum.BITTI_EKSIK).
    hashGuncellemeHatasi: boolean = false;
    // Bayrakları dolduran uyarılar; görev eksik biterse açıklamaya yazılır (bkz. eksikBitisAciklamasi).
    icraProSenkronUyarisi: string = "";
    hashGuncellemeUyarisi: string = "";
    dayanaklar: Blob[] = []

    constructor() {
        super();
    }

    setStatus(status: TakipAcStatusEnum) {
        this.status = status;
    }

    /**
     * Koşu başında çağrılır. Görev verisi önceki koşunun cevabı olabilir (hazırla görevinin
     * cevabı ödeme görevinin verisi olur) ve fromJson bayrakları da taşır; bayraklar yalnız
     * bu koşunun sonucunu yansıtmalı, yoksa başarılı ödeme BITTI_EKSIK biter (DEV-3302).
     */
    protected eksikBitisBayraklariniSifirla(): void {
        this.icraProSenkronHatasi = false;
        this.icraProSenkronUyarisi = "";
        this.hashGuncellemeHatasi = false;
        this.hashGuncellemeUyarisi = "";
    }

    /**
     * Görev BITTI_EKSIK bitecekse açıklamaya (description) yazılacak metin, bitmeyecekse "".
     * İşlem detay ekranları eksik satırın nedenini description'dan gösterir; uyarı yalnız
     * messages içinde kalırsa görünmez (DEV-3302). TevziAl handler'ları kart senkronu
     * bayrağını okumaz, onlar `kartSenkronuDahil` = false ile çağırır.
     */
    eksikBitisAciklamasi(kartSenkronuDahil: boolean = true): string {
        const uyarilar: string[] = [];
        if (kartSenkronuDahil && this.icraProSenkronHatasi)
            uyarilar.push(this.icraProSenkronUyarisi || "Takip UYAP'ta açıldı ancak icraPro kartına işlenemedi.");
        if (this.hashGuncellemeHatasi)
            uyarilar.push(this.hashGuncellemeUyarisi || "Takip UYAP'ta açıldı ancak hash kaydı güncellenemedi.");
        return uyarilar.join(" ");
    }

    /**
     * Ödeme isteği gönderilmeden önce hash kaydını "ödeme denendi, sonucu bilinmiyor"
     * durumuna çeker. Kesinti/çökme olursa yeniden başlatmada bu durum görülür ve
     * kullanıcıdan UYAP'tan manuel kontrol istenir.
     *
     * Servis bu durumu henüz tanımıyorsa hata yutulur; koruma devre dışı kalır ama
     * ödeme akışı kırılmaz.
     */
    async odemeDeneniyorIsaretle(hash: string): Promise<void> {
        await this.hashDurumuGuncelle(hash, ODEME_DENENIYOR_STATUS);
    }

    /** Ödeme kesin bir hatayla sonuçlandığında kaydı tekrar tevzi durumuna alır. */
    async odemeDeneniyorGeriAl(hash: string): Promise<void> {
        await this.hashDurumuGuncelle(hash, TEVZI_STATUS);
    }

    private async hashDurumuGuncelle(hash: string, status: string): Promise<void> {
        if (!hash)
            return;
        try {
            await app.$http.put("/api/v1/uyap-takip-hash", {hash, status});
        } catch (e) {
            console.error("hashDurumuGuncelle", status, e);
        }
    }

    /**
     * Takip UYAP'ta açıldıktan sonra hash kaydını "acildi" durumuna çeker; icra dairesi
     * ve esas no da kayda yazılır.
     *
     * Bu adım takip açıldıktan (ödeme varsa harç ödendikten) SONRA çalışır: hata hiçbir
     * zaman "takip açılamadı" demek değildir, bu yüzden dışarı fırlatılmaz (DEV-3302).
     * Kayıt bu arada silinmişse sunucu 404 döner; görev `hashGuncellemeHatasi` bayrağıyla
     * BITTI_EKSIK kapanır ve esas no uyarı mesajında kalır. Dışarıdan gelen dosyalarda hash
     * boştur, güncellenecek kayıt yoktur; istek atılmaz (atılırsa 422 dönüyordu).
     * Bayrak koşu başında eksikBitisBayraklariniSifirla ile iner.
     */
    protected async hashKaydiniAcildiYap(hash: string): Promise<void> {
        if (!hash)
            return;
        try {
            await app.$http.put("/api/v1/uyap-takip-hash", {
                hash,
                icra_dairesi: this.takipBilgileri.icraDairesi,
                dosya_esas_no: this.takipBilgileri.dosyaEsasNo,
                status: ACILMIS_TAKIP_STATUS
            });
        } catch (e) {
            this.hashGuncellemeHatasi = true;
            const mesaj = e?.response?.data?.message ?? e?.message ?? e;
            this.hashGuncellemeUyarisi =
                "Takip UYAP'ta açıldı (" + this.takipBilgileri.icraDairesi + " " +
                this.takipBilgileri.dosyaEsasNo + ") ancak hash kaydı güncellenemedi; bu dosya " +
                "yeniden gönderilirse mükerrer takip kontrolü çalışmayabilir: " + mesaj;
            this.addMessage("warning", this.hashGuncellemeUyarisi);
        }
    }

    /**
     * hash-kontrol cevabı olumsuz geldiğinde reject edilecek hatayı üretir.
     * Kayıt "acildi" ise icra dairesi/esas no verilir ve hash silinemez,
     * "odeme_deneniyor" ise UYAP'tan kontrol istenir, aksi halde eski davranış korunur.
     */
    private mukerrerHatasiOlustur(hash: string, data: HashKontrolKayit | null): any {
        this.acilmisTakipBilgisi = null;
        const dosya = data?.dosya ?? null;

        if (isAcilmisTakipKaydi(data)) {
            this.acilmisTakipBilgisi = acilmisTakipBilgisiOlustur(data);
            return {
                message: acilmisTakipHataMesaji(this.acilmisTakipBilgisi),
                hash,
                dosya,
                acilmisTakipBilgisi: this.acilmisTakipBilgisi
            };
        }

        if (isOdemeDeneniyorKaydi(data)) {
            return {
                message: odemeDeneniyorHataMesaji(),
                hash,
                dosya
            };
        }

        return {
            message: 'Bu dosya için daha önce tevzi alınmış (mükerrer kayıt). Yeniden denemek için sağdaki çöp kutusundan hash kaydını silebilirsiniz.',
            hash,
            dosya
        };
    }

    mukerrerDosyaKontrol(hash: string, type: "xml" | "uyap"): Promise<any> {
        return new Promise(async (resolve, reject) => {
            //        //this.hashObj, this.tarafList, this.toplamTakibeEsasMiktar
            try {
                let isMukerrer: HashKontrolCevap = await app.$http.post('/api/v1/hash-kontrol/' + type, {
                    hash: hash,
                    type: type,
                });
                if (isMukerrer.success == true) {
                    this.acilmisTakipBilgisi = null;
                    return resolve({});
                } else {
                    return reject(this.mukerrerHatasiOlustur(hash, isMukerrer.data));
                }
            } catch (e) {
                return reject({message: ("Hash sorgulanamadı. " + e.message)});
            }
        })
    }

    /**
     * UYAP'ta açılan takibin esas no / icra dairesi bilgisini icraPro kartına işler.
     *
     * Tek istek: büro referansı (XML'den), takip numarası ve UYAP'tan dönen açılış
     * bilgisi birlikte gider; icra dairesi çözümlemesini sunucu yapar. Hedef büroyu
     * isteğin Tenant-Id header'ı değil referans belirler — icraproweb bürosu çok
     * kullanıcılı olduğunda kullanıcının e-imza bürosu farklıdır ve kart orada
     * bulunamıyordu.
     *
     * Bu adım UYAP ödemesinden SONRA çalışır: hata hiçbir zaman "takip açılamadı"
     * demek değildir, bu yüzden dışarı fırlatılmaz. Görev `icraProSenkronHatasi`
     * bayrağıyla BITTI_EKSIK olarak kapanır ve esas no uyarı mesajında kalır.
     */
    async setTakipBilgileriToIcraPro(dosyaBilgileri: any, birimID: string= ""): Promise<void> {
        let dosyaBelirleyici = dosyaBilgileri.dosyaBelirleyici;
        if(dosyaBilgileri.isIcraProXml()){
            return app.$imza.icrapro80VeriAktar(this.takipBilgileri.dosyaEsasNo,this.takipBilgileri.icraDairesi,dosyaBelirleyici.id,dosyaBelirleyici.klasor);
        }

        if (!dosyaBilgileri.isIcraProWebXml() || !(dosyaBelirleyici?.id > 0))
            return;

        // Yalnız tevzi alınıp takip açılmadıysa esas no yoktur. İstek 422 dönüp bayrağı
        // dolduruyordu; bayrak hazırla cevabıyla ödeme görevine de taşınıyordu (DEV-3302).
        if (!this.takipBilgileri.dosyaEsasNo)
            return;

        try {
            // $http response interceptor'ı gövdeyi döndürüyor, AxiosResponse'u değil.
            const cevap: { sonuc?: string; uyari?: string | null } = await app.$http.patch(
                "/api/v1/takip/uyap-acilis-bilgisi",
                {
                    takip_ref: dosyaBelirleyici.takipRef || null,
                    takip_id: Number(dosyaBelirleyici.id),
                    borclu_adi: dosyaBelirleyici.name || null,
                    dosya_esas_no: this.takipBilgileri.dosyaEsasNo,
                    icra_dairesi: this.takipBilgileri.icraDairesi,
                    uyap_birim_id: birimID,
                }
            );

            if (cevap?.uyari)
                this.addMessage("warning", cevap.uyari);
        } catch (e) {
            // `catch (e: any)` TS 4.0+ sözdizimi; vue-imza-app TS 3.8.3 ile fork-ts-checker'da
            // TS1196 verip build'i kırıyor. 3.8'de catch değişkeni zaten `any`.
            this.icraProSenkronHatasi = true;
            const mesaj = e?.response?.data?.mesaj ?? e?.message ?? e;
            this.icraProSenkronUyarisi =
                "Takip UYAP'ta açıldı (" + this.takipBilgileri.icraDairesi + " " +
                this.takipBilgileri.dosyaEsasNo + ") ancak icraPro kartına işlenemedi: " + mesaj;
            this.addMessage("warning", this.icraProSenkronUyarisi);
        }
    }

    async getImzasizUdfFromUyap(dosyaId: string): Promise<void> {
        if (!gecerliUdf(this.imzasizUdf)) {
            let takibTalebi = await app.$uyap.TakipTalebiIndir(dosyaId)
                .run({
                    download: true,
                    filename: "takip_talebi.udf"
                });
            this.imzasizUdf = takibTalebi.file;
        }
        // eslint-disable-next-line no-console
        console.log("this.imzasizUdf", this.imzasizUdf);
    }

    async mtsGetImzasizUdfFromUyap(dosyaId: string): Promise<void> {
        if (!gecerliUdf(this.imzasizUdf)) {
            let takibTalebi = await app.$uyap.MtsTakipTalebiIndir()
                .run({
                    dosyaId: dosyaId
                });
            this.imzasizUdf = takibTalebi.file;
        }
        // eslint-disable-next-line no-console
        console.log("this.imzasizUdf", this.imzasizUdf);
    }

    setTakipBilgileriFromTakipAcilisEvraklariResponse(): Promise<any> {
        try {
            let div = document.createElement("div");
            // eslint-disable-next-line no-console
            console.log("takipAcilisEvraklariResponse",this.takipAcilisEvraklariResponse);
            div.innerHTML = this.takipAcilisEvraklariResponse.message;
            let nodeList = div.querySelectorAll("strong");
            if (nodeList.length == 0)
                return Promise.reject({message: "Takip açılış yapılamadı !"});
            this.takipBilgileri.icraDairesi = nodeList[0].innerHTML;
            this.takipBilgileri.dosyaEsasNo = nodeList[1].innerHTML;
            return Promise.resolve();
        } catch (e) {
            return Promise.reject(e);
        }
    }



    protected async takipTalebiniIndir(dosyaId: string, isMts: boolean): Promise<void> {
        if (isMts)
            await this.mtsGetImzasizUdfFromUyap(dosyaId);
        else
            await this.getImzasizUdfFromUyap(dosyaId);
    }

    /** Görev yürütücüsü çalışıyor mu (tarayıcıdaki Hemen Aç'ta çalışmaz). */
    protected botCalisiyorMu(): boolean {
        return app.$store?.getters?.getPlayerDurum === PlayerDurum.oto;
    }

    /**
     * Gönderim başladığında çalışan görev yürütücüsü bu arada durduruldu mu. "durduruluyor" birkaç
     * saniye sonra "dur"a dönüyor; UyapRequest.botDurduruldu gibi oto dışındaki her durum durdurmadır.
     */
    protected botDurdurulduMu(botCalisiyordu: boolean): boolean {
        const durum = app.$store?.getters?.getPlayerDurum;
        return botCalisiyordu && durum !== undefined && durum !== PlayerDurum.oto;
    }

    /**
     * Takip talebini imzalayıp açılış evraklarıyla gönderir. UYAP takip talebini "değişiklik
     * yapmadan imzalayıp gönderin" diye reddederse aynı dosyada evrak yeniden indirilip imzalanır
     * ve tekrar gönderilir (DEV-3880). Başka hatalarda tekrar yok: zaman aşımında evrak gitmiş olabilir.
     * Yeni tevzi alınmaz; ödeme ancak bu fonksiyon başarıyla dönerse yapılır.
     */
    async takipAcilisEvraklariniGonder(tevziDosyaId: string,isMts: boolean = false): Promise<EvrakGonderCevap> {
        const botCalisiyordu = this.botCalisiyorMu();
        let denemeHakki = TAKIP_TALEBI_DENEME_SAYISI;
        let reddedildi = false;
        let oncekiOzet: string | null = null;
        for (let deneme = 1; deneme <= denemeHakki; deneme++) {
            let icerikAyni: boolean | null = null;
            try {
                if (deneme > 1) {
                    // İndirme fonksiyonları eldeki evrakı yeniden indirmiyor; önce bırakılır.
                    this.imzasizUdf = undefined as any;
                    await this.takipTalebiniIndir(tevziDosyaId, isMts);
                    const yeniOzet = await udfOzeti(this.imzasizUdf);
                    icerikAyni = oncekiOzet != null && yeniOzet != null ? oncekiOzet === yeniOzet : null;
                } else if (!gecerliUdf(this.imzasizUdf) && !app.$store.getters.isEicraproDevMode) {
                    // Hemen Aç'ta tevzi sırasındaki indirme hatası yutuluyor; evrak yoksa burada indirilir.
                    // Geliştirici modunda imza evrakı kullanmıyor, sahte indirme de boş dönüyor.
                    await this.takipTalebiniIndir(tevziDosyaId, isMts);
                }
                await this.dokumanImzala();
                let formData: any;
                if(isMts)
                    formData = this.getDosyaAcilisEvraklarFormDataForMts(tevziDosyaId);
                else
                    formData = this.getDosyaAcilisEvraklarFormData(tevziDosyaId);
                console.log("FormData entries:");
                for (let [key, value] of formData.entries()) {
                    console.log(key, value);
                }
                let evraklarResult = await app.$uyap.DosyaAcilisEvrakGonder().run(
                    {
                        formData: true,
                        data: formData
                    })
                if (reddedildi) {
                    istekOlcumu.evrakDenemesiKaydet({n: deneme, sonuc: 'kabul', icerikAyni});
                    takipTalebiSigortasiniSifirla();
                }
                this.addMessage('success', "Dosya açılış evrakları gönderildi.");
                this.takipAcilisEvraklariResponse = evraklarResult;
                return Promise.resolve(evraklarResult);
            } catch (e) {
                const icerikReddi = takipTalebiIcerikRetMi(e);
                if (icerikReddi) {
                    reddedildi = true;
                    istekOlcumu.evrakDenemesiKaydet({n: deneme, sonuc: 'red', icerikAyni});
                    if (deneme == 1)
                        denemeHakki = retSonrasiDenemeHakki();
                    if (deneme < denemeHakki && !this.botDurdurulduMu(botCalisiyordu)) {
                        oncekiOzet = await udfOzeti(this.imzasizUdf);
                        this.addMessage('warning', "UYAP takip talebini kabul etmedi (deneme " + deneme + "/" + denemeHakki +
                            "); takip talebi yeniden indirilip imzalanıyor.");
                        continue;
                    }
                }
                let hata = e;
                if (icerikReddi) {
                    hata = takipTalebiIcerikRetHatasi(e);
                    // Durdur'la kesilen dosya sigortaya sayılmaz; yalnız bütün denemeleri reddedilen sayılır.
                    if (denemeHakki > 1 && deneme == denemeHakki) {
                        sonucsuzTekrar++;
                        if (sonucsuzTekrar == TAKIP_TALEBI_SIGORTA_ESIGI)
                            this.addMessage('warning', "UYAP, tekrar denenen son " + sonucsuzTekrar + " dosyanın hiçbirinde " +
                                "takip talebini kabul etmedi; bundan sonra reddedilen her " + TAKIP_TALEBI_SIGORTA_YOKLAMA +
                                " dosyadan yalnız biri tekrar denenecek.");
                    }
                }
                this.setStatus(TakipAcStatusEnum.OPEN_FAIL);
                // UYAP hata yönlendirmesi (ör. evrak gönderme aksaklığı) varsa ham metin yerine yönlendirici mesaj göster.
                const yonlendirme = matchUyapError(hata);
                this.addMessage('error', yonlendirme ? yonlendirme.userMessage : hata.message);
                return Promise.reject(hata)
            }
        }
        // Her tur ya döner ya reddeder; buraya gelinmez.
        throw new Error("Takip talebi gönderilemedi.");
    }

    getDosyaAcilisEvraklarFormDataForMts(tevziDosyaId: any): FormData {
        //acilisda mandatory true gidiyor ///
        let gonderilecekEvraklar = {
            "tur": "",
            "turAciklama": "",
            "dosyaId": tevziDosyaId,
            "esasNo": "",
            "data": [
                {
                    "tur": "ICR_TAKIP_TLP",
                    "turAciklama": "Takip Talebi",
                    "aciklama": "Takip Talebi",
                    "evrak": this.imzaliUdf,
                    "dosyaEkEvrak": []
                },
                {
                    "tur": "CZM_VEKALETNAME",
                    "turAciklama": "Vekaletname",
                    "aciklama": "Vekaletname",
                    "evrak": this.getVekaletBlob(),
                    "dosyaEkEvrak": []
                }]
        };
        this.getDayanaklarBlobArray().forEach((dayanakBlob) => {
            gonderilecekEvraklar.data.push(
                {
                    "tur": "MTS_TAKIBIN_DAYANAGI",
                    "turAciklama": "Takibin Dayanağı",
                    "aciklama": "Takibin Dayanağı",
                    "evrak": dayanakBlob,
                    "dosyaEkEvrak": []
                }
            );
        })
        // eslint-disable-next-line no-console
        console.log("gonderilecekEvraklar", gonderilecekEvraklar);
        let evraklar: any[] = gonderilecekEvraklar.data;
        let items: any[] = [];
        let anaEvrakCounter = 1;
        let counter = 1;
        let form = new FormData();
        let defaultTurlist = [
            ".udf",
            ".pdf",
            ".jpg",
            ".jpeg",
            ".png",
            ".tif",
            ".tiff",
            ".UDF",
            ".PDF",
            ".JPG",
            ".JPEG",
            ".PNG",
            ".TIF",
            ".TIFF"
        ];
        let takipTalebiTurList = [
            ".jpg",
            ".udf",
            ".JPG",
            ".UDF"
        ];
        let turList = [];
        let ekEvrakTurList = [];
        evraklar.forEach((evrak, i) => {
            let evrakName = "evrak_" + anaEvrakCounter + ".udf";
            if (typeof evrak.evrak.name != "undefined") {
                evrakName = evrak.evrak.name;
            } else {
                let fileExt = Mixins.getFileExtension(evrak.evrak.type);
                evrakName = "dosya_" + i + "." + fileExt;
            }
            if (evrak.tur == "ICR_TAKIP_TLP") {
                turList = [".udf",".UDF"];
                ekEvrakTurList = takipTalebiTurList;
                evrak.aciklama = "";
            } else {
                turList = defaultTurlist;
                ekEvrakTurList = defaultTurlist;
            }
            items.push({
                id: (new Date()).getTime(),
                evrakTuruOptionDVO: {
                    tur: evrak.tur,
                    label: evrak.turAciklama,
                    mandatory: true,
                    max: 1,
                    ekEvrakMax: 5,
                    acceptTypeList: turList,
                    ekEvrakAcceptTypeList: ekEvrakTurList,
                },
                tur: evrak.tur,
                turAciklama: evrak.turAciklama,
                mandatory: true,
                file: {},
                path: "C:\\fakepath\\" + evrakName,
                kullaniciEvrakAciklama: evrak.aciklama,
                parentId: -1,
                isVekalet: false,
                isVekaletBilgiFormu: false,
                fileId: anaEvrakCounter
            });
            // eslint-disable-next-line no-console
            console.log("EKLENEN EVRAK ", evrak.evrak);
            form.append("file_" + anaEvrakCounter, evrak.evrak, evrakName);
            // eslint-disable-next-line no-console
            console.log("F append => ", "file_" + anaEvrakCounter, evrak.evrak, evrakName);
            counter++;
            anaEvrakCounter = counter;
        })
        form.append("items", JSON.stringify(items));
        form.append("dosyaId", tevziDosyaId);
        return form;
    }

    getDosyaAcilisEvraklarFormData(tevziDosyaId: any): FormData {
        const defaultTurList = [
            ".udf", ".pdf", ".jpg", ".jpeg", ".png", ".tif", ".tiff",
            ".UDF", ".PDF", ".JPG", ".JPEG", ".PNG", ".TIF", ".TIFF"
        ];
        const ekEvrakTurList = [
            ".udf", ".pdf", ".jpg", ".png", ".jpeg", ".image/jpg", ".image/jpeg",
            ".tif", ".tiff", ".UDF", ".JPG", ".JPEG", ".PNG", ".PDF", ".TIF", ".TIFF"
        ];

        const gonderilecekEvraklar: any[] = [
            {
                tur: "ICR_TAKIP_TLP",
                turAciklama: "Takip Talebi",
                aciklama: "Takip Talebi",
                evrak: this.imzaliUdf,
                dosyaEkEvrak: [],
                acceptTypeList: [".udf", ".UDF"],
                mandatory: true
            },
            {
                tur: "CZM_VEKALETNAME",
                turAciklama: "Vekaletname",
                aciklama: "Vekaletname",
                evrak: this.getVekaletBlob(),
                dosyaEkEvrak: [],
                acceptTypeList: defaultTurList,
                mandatory: true
            }
        ];

        this.getDayanaklarBlobArray().forEach((dayanakBlob) => {
            gonderilecekEvraklar.push({
                tur: "MTS_TAKIBIN_DAYANAGI",
                turAciklama: "Takibin Dayanağı",
                aciklama: "Takibin Dayanağı",
                evrak: dayanakBlob,
                dosyaEkEvrak: [],
                acceptTypeList: defaultTurList,
                mandatory: false
            });
        });

        const items: any[] = [];
        let fileCounter = 1;
        const form = new FormData();

        gonderilecekEvraklar.forEach((evrak, i) => {
            const evrakName = typeof evrak.evrak.name !== "undefined"
                ? evrak.evrak.name
                : "dosya_" + i + "." + Mixins.getFileExtension(evrak.evrak.type);

            const anaEvrakId = (new Date()).getTime();
            const anaEvrakFileId = fileCounter;

            const item: any = {
                id: anaEvrakId,
                evrakTuruOptionDVO: {
                    tur: evrak.tur,
                    label: evrak.turAciklama,
                    max: 1,
                    ekEvrakMax: 5,
                    sablonTurKodu: "",
                    acceptTypeList: evrak.acceptTypeList,
                    ekEvrakAcceptTypeList: ekEvrakTurList
                },
                tur: evrak.tur,
                turAciklama: evrak.turAciklama,
                file: {},
                path: "C:/fakepath/" + evrakName,
                kullaniciEvrakAciklama: evrak.aciklama,
                parentId: -1,
                isVekalet: false,
                isVekaletBilgiFormu: false,
                fileId: anaEvrakFileId
            };

            if (evrak.mandatory) {
                item.mandatory = true;
            }

            items.push(item);
            form.append("file_" + anaEvrakFileId, evrak.evrak, evrakName);
            fileCounter++;

            evrak.dosyaEkEvrak.forEach((ekEvrak: any, ekIndex: number) => {
                const ekEvrakFileId = fileCounter;
                const ekEvrakName = ekEvrak.name || ("ek_dosya_" + ekEvrakFileId + "." + Mixins.getFileExtension(ekEvrak.type));
                items.push({
                    id: (new Date()).getTime(),
                    evrakTuruOptionDVO: {
                        tur: evrak.tur,
                        label: evrak.turAciklama + " - Ek Evrak",
                        max: 1,
                        ekEvrakMax: 5,
                        sablonTurKodu: "",
                        acceptTypeList: defaultTurList,
                        ekEvrakAcceptTypeList: ekEvrakTurList
                    },
                    tur: evrak.tur,
                    turAciklama: evrak.turAciklama + " - Ek Evrak EK " + (ekIndex + 1),
                    file: {},
                    path: "C:/fakepath/" + ekEvrakName,
                    kullaniciEvrakAciklama: "",
                    parentId: anaEvrakId,
                    isVekalet: false,
                    isVekaletBilgiFormu: false,
                    fileId: ekEvrakFileId
                });
                form.append("file_" + ekEvrakFileId, ekEvrak, ekEvrakName);
                fileCounter++;
            });
        });

        form.append("items", JSON.stringify(items));
        form.append("dosyaId", tevziDosyaId);
        return form;
    }

    getDayanaklarBlobArray(): Blob[] {
        return this.dayanaklar;
    }

    getVekaletBlob() {
        return this.vekalet;
        /*
        if (typeof this.par.vekalet.data == "string") {
            this.vekalet.data = this.vekalet.data.replace('<!--[CDATA[', '').replace(']]-->', '');
        }
        let vekaletB64 = this.vekalet.data;
        //vekalet base64 olarak geldiyse
        if (typeof vekaletB64 == "string" && vekaletB64.indexOf(',') > 0) {
            vekaletB64 = this.vekalet.data.split(',')[1];
        }
        // eslint-disable-next-line no-console
        console.log("this.vekalet",this.vekalet);
        console.log("this.imzaliUdf",this.imzaliUdf);
        this.vekaletBlob = Mixins.b64toBlob(vekaletB64, this.vekalet.mimeType);
        //application/octet-stream
        console.log("VEKALET BLOB",this.vekaletBlob);
         */
    }

    convertToISOFormat(dateStr: string) {
        // dateStr -> 09/05/2024 10:43:06
        const parts = dateStr.split(" "); // Boşluklardan böl
        const dateParts = parts[0].split("/"); // Tarihi '/' işaretlerinden böl
        const timeParts = parts[1].split(":"); // Saati ':' işaretlerinden böl

        const isoDateStr = `${dateParts[2]}-${dateParts[1]}-${
            dateParts[0]
        }T${timeParts.join(":")}`;
        return isoDateStr;
    }

    async dokumanImzala(): Promise<void> {
        //app.$imza.setSimulator();
        if(app.$store.getters.isEicraproDevMode){
            this.imzaliUdf = new Blob();
        }else{

            this.addMessage("info", "Takip talebi imzalanıyor.")

            //todo imzasiz udf bossa indir.

            let imzaliUdf = await app.$imza.dokumanImzala(this.imzasizUdf);
            this.addMessage('success', 'Takip talebi imzalandı')
            let imzaliUdfBlob: Blob = app.$uyapHelper.base64toBlob(imzaliUdf, "application/octet-stream");
            console.log("Dokuman Imzalandi", imzaliUdfBlob);
            const imzasizName =
                this.imzasizUdf &&
                typeof (this.imzasizUdf as File).name === "string"
                    ? (this.imzasizUdf as File).name.trim()
                    : "";
            const resolvedTakipTalebiName =
                imzasizName !== "" ? imzasizName : "takip_talebi.udf";
            this.imzaliUdf = new File([imzaliUdfBlob], resolvedTakipTalebiName, {
                type: "application/octet-stream",
            });

        }

    }
}

